import React from "react";
import { Link, NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";
import {
  SquaresFour, UsersThree, FileText, EnvelopeSimple,
  ChatCircleDots, UsersFour, Megaphone, MagnifyingGlass, SignOut, ChatsCircle,
  Lightning, Robot, Plugs, CurrencyDollar, CalendarBlank, Pulse, LinkedinLogo, Gear, BookOpen, Eye, Star
} from "@phosphor-icons/react";

const NAV = [
  { to: "/admin", label: "Overview", icon: SquaresFour, end: true },
  { to: "/admin/activity", label: "Activity", icon: Pulse },
  { to: "/admin/leads", label: "Leads", icon: UsersThree },
  { to: "/admin/research", label: "Research Agent", icon: MagnifyingGlass },
  { to: "/admin/agents", label: "AI Agents", icon: Robot },
  { to: "/admin/autopilot", label: "Autopilot", icon: Lightning },
  { to: "/admin/playbook", label: "Playbook", icon: BookOpen },
  { to: "/admin/founding", label: "Founding Cohort", icon: Star },
  { to: "/admin/templates", label: "Email Templates", icon: EnvelopeSimple },
  { to: "/admin/chat", label: "Website Chatbot", icon: ChatsCircle },
  { to: "/admin/outreach", label: "Outreach", icon: EnvelopeSimple },
  { to: "/admin/linkedin", label: "LinkedIn", icon: LinkedinLogo },
  { to: "/admin/replies", label: "Replies", icon: ChatCircleDots },
  { to: "/admin/applications", label: "Applications", icon: FileText },
  { to: "/admin/members", label: "Members", icon: UsersFour },
  { to: "/admin/meetings", label: "Meetings", icon: CalendarBlank },
  { to: "/admin/content", label: "Content", icon: Megaphone },
  { to: "/admin/integrations", label: "Integrations", icon: Plugs },
  { to: "/admin/budget", label: "AI Budget", icon: CurrencyDollar },
  { to: "/admin/settings", label: "Settings", icon: Gear },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen flex bg-[#F3F4F6]" data-testid="admin-layout">
      <aside className="w-64 bg-navy text-white flex flex-col border-r border-black/20">
        <Link to="/" className="px-5 py-5 border-b border-white/10 block" data-testid="admin-logo-link">
          <Logo variant="dark" size="sm" asLink={false} showTagline={true} taglineClassName="" testId="admin-logo" />
        </Link>
        <nav className="flex-1 py-4 px-2 space-y-0.5">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-white/10 text-white border-l-2 border-ember"
                    : "text-white/70 hover:text-white hover:bg-white/5 border-l-2 border-transparent"
                }`
              }
            >
              <Icon size={18} weight="regular" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 text-xs space-y-3">
          <a
            href="/portal"
            data-testid="admin-view-as-member"
            className="flex items-center gap-2 text-ember hover:text-white transition-colors uppercase tracking-widest text-[11px]"
          >
            <Eye size={14} /> View as member
          </a>
          <div>
            <div className="text-white/60 mb-1 uppercase tracking-widest text-[10px]">Signed in</div>
            <div className="text-white truncate mb-3" data-testid="admin-user-email">{user?.email || "—"}</div>
            <button
              onClick={onLogout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <SignOut size={16} /> Sign out
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
